import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { KnowledgeBaseLibraryZ } from "knowledge-base/_schemas/knowledge-base-article-schema";
import { z } from "zod";

export type KnowledgeBaseCategoryJSON = z.infer<typeof KnowledgeBaseCategorySchema.CategoryJSONZ>;
export type KnowledgeBaseCategoryParams = z.infer<typeof KnowledgeBaseCategorySchema.CategoryZ>;

/**
 * A place in a knowledge base that articles are filed under.
 *
 * `origin` says whether a person made this category or the summarizing agent did when an uploaded
 * document fitted nowhere. Both are legitimate; only one was reviewed, and the tree marks the
 * difference so a curator can find what arrived on its own.
 */
export const CategoryOriginZ = z.enum(["Authored", "SystemProvided", "AgentCreated"]);
export type CategoryOrigin = z.infer<typeof CategoryOriginZ>;

export default class KnowledgeBaseCategorySchema {
  public static readonly CategoryJSONZ = z.object({
    id: z.string(),
    ownerLegalEntityId: z.string(),
    library: KnowledgeBaseLibraryZ,
    name: z.string(),
    description: z.string().nullish(),
    parentCategoryId: z.string().nullish(),
    position: z.number(),
    origin: CategoryOriginZ,
  });

  public static readonly CategoryZ = z.object({
    id: z.string(),
    ownerLegalEntityId: z.string(),
    library: KnowledgeBaseLibraryZ,
    name: z.string(),
    description: z.string().optional(),
    parentCategoryId: z.string().optional(),
    position: z.number(),
    origin: CategoryOriginZ,
  });

  public static validate(values: unknown): KnowledgeBaseCategoryParams {
    return parseOrThrow(KnowledgeBaseCategorySchema.CategoryZ, values, {
      logError: true,
      schemaName: "KnowledgeBaseCategory",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is KnowledgeBaseCategoryParams {
    try {
      KnowledgeBaseCategorySchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: KnowledgeBaseCategoryJSON): KnowledgeBaseCategoryParams {
    const parsed = parseOrThrow(KnowledgeBaseCategorySchema.CategoryJSONZ, data, {
      logError: true,
      schemaName: "KnowledgeBaseCategory",
      operation: "deserialize",
      payloadSample: data,
    });

    return {
      id: parsed.id,
      ownerLegalEntityId: parsed.ownerLegalEntityId,
      library: parsed.library,
      name: parsed.name,
      description: parsed.description ?? undefined,
      parentCategoryId: parsed.parentCategoryId ?? undefined,
      position: parsed.position,
      origin: parsed.origin,
    };
  }

  public static serialize(category: KnowledgeBaseCategoryParams): KnowledgeBaseCategoryJSON {
    const parsed = KnowledgeBaseCategorySchema.validate(category);

    return {
      id: parsed.id,
      ownerLegalEntityId: parsed.ownerLegalEntityId,
      library: parsed.library,
      name: parsed.name,
      description: parsed.description,
      parentCategoryId: parsed.parentCategoryId,
      position: parsed.position,
      origin: parsed.origin,
    };
  }
}

/**
 * Which knowledge bases the reader can open, as the server works it out.
 *
 * More than the reader's own two: a firm doing work for several clients can also open each of
 * those clients' external libraries, read-only.
 */
export const LibraryJSONZ = z.object({
  library: KnowledgeBaseLibraryZ,
  ownerLegalEntityId: z.string(),
  ownerName: z.string(),
  isOwn: z.boolean(),
  canAuthor: z.boolean(),
});

export type LibraryJSON = z.infer<typeof LibraryJSONZ>;
